import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorBoundary, Retry } from '@components/common';
import { calcShortcutInterval } from '@utils';
import { getUserProfile, intervalSelector, promptFeatureSelector } from '@store/common/selectors';
import OverviewDatePicker from './components/datepicker/datepicker';
import DashboardTabs from '../common/Dashboard/DashboardTabs';
import { Wrapper, DashboardPanel, Header } from './styled';
import { EQueryIdType, TimeSpan } from './types';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { useIds } from './hooks/useIds';
import moment from 'moment-timezone';
import '@conviva/organism-components/styles.css';
import {
  UserTimeline,
  SessionTimelineTableItem,
  EmptySessionInfo,
  SessionTimelineTableHeader,
  ItemRenderer,
  getEventStatistic,
  handleEventList,
} from '@conviva/organism-components';
import { DASHBOARD_TYPE, DATE_SHORTCUTS as SHORTCUTS } from '@src/utils/constants';
import { useTimelineOverview } from './hooks/useTimelineOverview';
import {
  useGetAppDataTimelineEventList,
  useGetPersistence,
  usePutPersistence,
} from './utils/queryHooks';
import Search from 'antd/es/input/Search';
import { Select } from 'antd';
import { getAppViewerModuleTempDashboardUrl } from '../common/Dashboard/utils/getTempDashboardUrl';
import { useHistory } from 'react-router-dom';
import { createTempDashboard } from '@src/store/tempDashboard/actions';
import { EmptyArray, idOptions } from './constants';
import { SearchHelpText } from './components/SearchHelpText';
import useGetStreamResponse from '@src/pages/Prompt/hooks/useGetStreamResponse';
import { useAtom, useSetAtom } from 'jotai';
import {
  SummaryStatus,
  allEventList,
  copyTextAtom,
  summaryCollapse,
  status,
  selectedEventsAtom,
  eventFilterOnAtom,
} from './atom';
import { TIMELINE_SUMMARY_QUESTION, getBotAnswerText } from '../prompt/utils';
import { PROMPT_ANSWER } from '@src/utils/sensor/track';
import apptracker from '@src/utils/sensor/apptracker';
import AISummary from './components/ai-summary/AISummary';
import { isEmpty } from 'lodash';

interface IProps {
  id: string;
  viewerId: string | undefined;
  clientId: string;
  interval?: string;
}

const getDefaultTimeSpan = (defaultTimeSpan): TimeSpan => {
  let [from, to] = calcShortcutInterval(SHORTCUTS.last6Hours);
  if (defaultTimeSpan) {
    [from, to] = defaultTimeSpan.split('/').map((x) => moment(x));
    return {
      from: from.format(),
      to: to.format(),
      shortcut: 'custom',
    };
  }
  return {
    from: from.format(),
    to: to.format(),
    shortcut: SHORTCUTS.last6Hours,
  };
};

const { baseUrl } = window.conviva;

const AppSessionTimeline: React.FC<IProps> = ({
  id = 'user-timeline',
  viewerId = '',
  clientId,
  interval: defaultTimeSpan,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const timezone = moment().tz();
  const { customer_id } = useSelector(getUserProfile);
  const hasIds = !!(viewerId || clientId);
  const selectedTrendMetric = useSelector(selectedMetricSelector);
  const globalInterval: {
    from: string;
    to: string;
    shortcut: string;
    granularity: string;
  } = useSelector(intervalSelector);
  const [selectedSession, setSelectedSession] =
    useState<SessionTimelineTableItem>(EmptySessionInfo);
  const [timeSpan, setTimeSpan] = useState<TimeSpan>(getDefaultTimeSpan(defaultTimeSpan));
  const [interval, setInterval] = useState<string>(defaultTimeSpan || '');
  const [idType, setIdType] = useState<EQueryIdType>(EQueryIdType.userId);
  const [nwEnabled, setNWEnabled] = useState<boolean>(false);
  const [selectedEvents, setSelectedEvents] = useAtom(selectedEventsAtom);
  const [eventFilterOn, setEventFilterOn] = useAtom(eventFilterOnAtom);

  const handleIdSelectChanged = (v) => {
    history.replace(`${baseUrl}/dashboard/user-timeline/`);
    setIdType(v);
  };
  const { data: configs } = useGetPersistence('user-timeline');
  const { mutate: updatePersistence } = usePutPersistence('user-timeline');
  const { idGroupData, selectedClientId, selectedViewerId, idLoading } = useIds({
    id,
    viewerId,
    clientId,
    selectedTrendMetric,
    interval,
    customer_id,
    idType,
    handleIdSelectChanged,
  });
  const { summaryData, sessionTableData, timelineLoading } = useTimelineOverview({
    viewerId: selectedViewerId,
    clientId: selectedClientId,
    interval,
  });
  const { data: timelineData, isFetching: eventListLoading } = useGetAppDataTimelineEventList(
    selectedViewerId,
    interval,
    selectedClientId,
    selectedSession.sessionId,
    nwEnabled,
  );

  useEffect(() => {
    const enableNetworkRequest = configs?.configuration?.enableNW;
    if (enableNetworkRequest !== undefined) {
      setNWEnabled(enableNetworkRequest);
    }
  }, [configs]);

  useEffect(() => {
    if (viewerId) {
      setIdType(EQueryIdType.userId);
      return;
    }
    if (clientId) {
      setIdType(EQueryIdType.clientId);
      return;
    }
  }, [viewerId, clientId]);

  useEffect(() => {
    if (defaultTimeSpan) {
      return;
    }
    const { from, to, shortcut } = globalInterval;
    if (from && to) {
      setTimeSpan({
        from,
        to,
        shortcut,
      });
      setInterval(`${from}/${to}`);
    }
  }, []);

  useEffect(() => {
    let item: SessionTimelineTableItem = EmptySessionInfo;
    for (const i of sessionTableData) {
      if ((i as SessionTimelineTableHeader).isHeader) {
        continue;
      }
      item = i as SessionTimelineTableItem;
      break;
    }
    setSelectedSession(item);
  }, [sessionTableData]);

  const handleSelectedSessionChange = (v: SessionTimelineTableItem) => {
    setSelectedSession(v);
  };

  const dateChangeFn = (obj) => {
    const { from, to } = obj;
    setTimeSpan(obj);
    setInterval(`${from}/${to}`);
  };

  const handleOnSearch = (v) => {
    if (!v?.trim()) {
      return;
    }
    let viewerId;
    let clientId;
    let dashboardTitle = 'User';
    if (idType === EQueryIdType.userId) {
      viewerId = v;
    } else if (idType === EQueryIdType.clientId) {
      clientId = v;
      dashboardTitle = 'Client';
    }
    const redirectHandler = ({ id: tabId }) => {
      history.push(getAppViewerModuleTempDashboardUrl({ viewerId, clientId }, tabId));
    };
    dispatch(
      createTempDashboard(
        DASHBOARD_TYPE.APP_VIEWER_MODULE,
        { viewerId, clientId },
        `${dashboardTitle} - ${v}`,
        {
          redirectHandler,
        },
      ),
    );
  };

  const handleNWToggleChange = () => {
    setNWEnabled(!nwEnabled);
    updatePersistence({ enableNW: !nwEnabled });
  };

  const handleSelectedEventsChange = (v: string[]) => {
    setSelectedEvents(v);
  };

  const handleEventFilterOnChange = (v: boolean) => {
    setEventFilterOn(v);
  };

  // AI summary related start =====================
  // because virtual list will destroy dom node, so put component state into atom and this component
  const { isLoading, hasStreamingEnded, botData, getStream } =
    useGetStreamResponse('timeline-summary');

  const [allEventsKeyList, setAllEventsList] = useAtom(allEventList);
  const setCollapse = useSetAtom(summaryCollapse);
  const setCopyText = useSetAtom(copyTextAtom);
  const [summaryState, setSummaryState] = useAtom(status);

  const showPromptAISummary = useSelector(promptFeatureSelector);

  const cancelController = useRef<AbortController>();

  const sessionId = selectedSession.sessionId;
  const requestBody = timelineData?.requestBody;

  const handleGenerateClick = useCallback(() => {
    if (!(requestBody && Object.keys(requestBody).length > 0)) {
      return;
    }
    // pass the canceled event list to the LLM
    const filterList = allEventsKeyList.filter((key) => {
      return !selectedEvents.includes(key);
    });
    cancelController.current = new AbortController();
    getStream(
      {
        timelineEventQuery: requestBody,
        userFilterNameList: filterList,
      },
      cancelController.current,
    );
    setCollapse(false);
  }, [allEventsKeyList, getStream, requestBody, selectedEvents, setCollapse]);

  useEffect(() => {
    // when the page is going to destroy, reset the atom status
    return () => {
      setSummaryState(SummaryStatus.INIT);
      setCollapse(false);
      setCopyText('Copy Response');
      cancelController.current?.abort();
    };
  }, []);

  useEffect(() => {
    // filter event list changed
    if (summaryState !== SummaryStatus.INIT) {
      setSummaryState(SummaryStatus.INIT);
      cancelController.current?.abort();
    }
  }, [selectedEvents, allEventsKeyList, sessionId, requestBody]);

  useEffect(() => {
    if (isLoading) {
      setSummaryState(SummaryStatus.GENERATING);
    }
    if (hasStreamingEnded) {
      setSummaryState(SummaryStatus.GOT_LATEST_CONTENT);
      const promptAnswer = getBotAnswerText(botData);
      apptracker(PROMPT_ANSWER, {
        questionAndAnswer: `${TIMELINE_SUMMARY_QUESTION}|||${promptAnswer}`,
        hasMetric: 'no',
      });
    }
  }, [isLoading, hasStreamingEnded]);
  // AI summary end =====================

  const eventList = timelineData?.eventList ?? EmptyArray;

  useEffect(() => {
    const { eventCount } = getEventStatistic(handleEventList(eventList));
    const allEventList = Object.keys(eventCount);
    setAllEventsList(allEventList);
    if (!eventFilterOn && !isEmpty(eventCount)) {
      setSelectedEvents?.(allEventList);
    }
  }, [eventFilterOn, eventList, setAllEventsList, setSelectedEvents]);

  const isTimelineEmpty = eventList.length === 0;
  const topItemList = useMemo(() => {
    if (!selectedSession.sessionId || isTimelineEmpty) {
      return [{ id: 'empty-view' }];
    }
    const res = [{ id: 'session-info' }];
    if (selectedEvents && selectedEvents.length > 0) {
      res.push({ id: 'session-summary' });
    }
    if (showPromptAISummary) {
      res.push({ id: 'ai-summary' });
    }
    res.push({ id: 'start' });
    return res;
  }, [isTimelineEmpty, selectedEvents, selectedSession.sessionId, showPromptAISummary]);
  const bottomItemList = useMemo(() => {
    if (!selectedSession.sessionId || isTimelineEmpty) {
      return EmptyArray;
    }
    return [{ id: 'end' }];
  }, [isTimelineEmpty, selectedSession]);

  const itemRenderers: ItemRenderer = useMemo(
    () => ({
      'ai-summary': () => (
        <div>
          <ErrorBoundary>
            <AISummary
              handleGenerateClick={handleGenerateClick}
              hasStreamingEnded={hasStreamingEnded}
              botData={botData}
            />
          </ErrorBoundary>
        </div>
      ),
    }),
    [botData, handleGenerateClick, hasStreamingEnded],
  );

  return (
    <Wrapper>
      <DashboardPanel>
        <DashboardTabs id={id} />
      </DashboardPanel>
      {hasIds ? (
        <Retry isLoading={idLoading || timelineLoading}>
          <UserTimeline
            timelineData={eventList}
            timelineConfig={{
              timelineItems: {
                topItemList,
                bottomItemList,
              },
              itemRenderers,
            }}
            timelineLoading={eventListLoading}
            idGroupData={idGroupData}
            summaryData={summaryData}
            sessionTableData={sessionTableData}
            selectedSession={selectedSession}
            onSelectedSessionChange={handleSelectedSessionChange}
            headerRightComp={
              <OverviewDatePicker dateChangeFn={dateChangeFn} selectedTimeSpan={timeSpan} />
            }
            enableNetworkRequest={nwEnabled}
            onNetworkRequestToggleChange={handleNWToggleChange}
            timezone={timezone}
            selectedEvents={selectedEvents}
            onSelectedEventsChange={handleSelectedEventsChange}
            eventFilterOn={eventFilterOn}
            onEventFilterOnChange={handleEventFilterOnChange}
          />
        </Retry>
      ) : (
        <>
          <Header>
            <Search
              addonBefore={
                <Select
                  defaultValue={EQueryIdType.userId}
                  options={idOptions}
                  onChange={handleIdSelectChanged}
                  value={idType}
                />
              }
              placeholder={
                idType === EQueryIdType.userId
                  ? 'Please input a User ID'
                  : 'Please input a Client ID'
              }
              allowClear
              onSearch={handleOnSearch}
              style={{ width: 500 }}
            />
          </Header>
          <SearchHelpText />
        </>
      )}
    </Wrapper>
  );
};

export default AppSessionTimeline;
